import { AppItem, Button, NavDrawer, NavDrawerBody, NavDrawerHeader, NavItem } from '@fluentui/react-components';
import { DismissRegular } from '@fluentui/react-icons';
import { useLocation, useNavigate } from '@tanstack/react-router';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAddInConfig } from '../../../hooks/add-in-config.js';
import { useStyles } from './_styles.js';
import { getIconComponent } from './_tools.js';

export function Sidebar() {
    const styles = useStyles();
    const navigate = useNavigate();
    const location = useLocation();
    const [isOpen, setIsOpen] = useState(true);

    const { data } = useAddInConfig();

    const isMac = useMemo(() => navigator.platform.toUpperCase().indexOf('MAC') >= 0, []);
    const shortcutLabel = useMemo(() => (isMac ? '⌘+B' : 'Ctrl+B'), [isMac]);

    const selectedValue = useMemo(() => location.pathname || '/', [location.pathname]);

    const handleToggle = useCallback(() => {
        setIsOpen((prev) => !prev);
    }, []);

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            // Toggle sidebar with Ctrl+B (Windows/Linux) or Cmd+B (Mac)
            if ((event.ctrlKey || event.metaKey) && event.key === 'b') {
                event.preventDefault();
                handleToggle();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [handleToggle]);

    const handleNavigate = (path: string) => {
        navigate({ to: path });
    };

    if (!data) {
        return null;
    }

    return (
        <NavDrawer open={isOpen} type="overlay" className={styles.sidebar} selectedValue={selectedValue}>
            <NavDrawerHeader>
                <Button appearance="subtle" icon={<DismissRegular />} onClick={handleToggle} />
            </NavDrawerHeader>

            <NavDrawerBody>
                <AppItem value="/dashboard" onClick={() => handleNavigate('/dashboard')}>
                    Toggle with {shortcutLabel}
                </AppItem>

                {data.body.navigation.flatMap((widget) => {
                    // A widget without children is itself the navigation target.
                    const items = widget.children.length > 0 ? widget.children : [widget];

                    return items.map((item) => (
                        <NavItem
                            key={item.widgetId}
                            icon={getIconComponent(widget.icon)}
                            value={'/' + item.widgetId}
                            onClick={() => handleNavigate('/' + item.widgetId)}
                        >
                            {item.label}
                        </NavItem>
                    ));
                })}
            </NavDrawerBody>
        </NavDrawer>
    );
}
